import React from 'react';
import { Hourglass, Mail, Scale, UserCheck } from 'lucide-react';
import { Badge } from '../ui';
import { AttentionItem } from '../../hooks/useDashboard';

/**
 * "Needs your attention · N" queue per `Dashboard v2.dc.html` — per-RFQ rows
 * with an icon chip, the real one-line reason, an optional Robbie insight
 * chip (Badge robbie tone; populated by the insights task later), and the
 * action link. Presentational: reasons are derived in `useDashboard` from
 * stored state only. Renders nothing when nothing needs attention — the
 * "Awaiting your review" KPI tile already says so honestly.
 */
interface AttentionListProps {
    items: AttentionItem[];
    onAction: (item: AttentionItem) => void;
    className?: string;
}

const MAX_ITEMS = 4;

const chipFor: Record<AttentionItem['reason'], { icon: React.ElementType; chipClass: string; iconClass: string }> = {
    'quotes-review': { icon: Scale, chipClass: 'bg-action-bg border-action-border', iconClass: 'text-action' },
    'quotes-open': { icon: Scale, chipClass: 'bg-action-bg border-action-border', iconClass: 'text-action' },
    'supplier-replies': { icon: Mail, chipClass: 'bg-warn-bg border-warn-border', iconClass: 'text-warn-ink' },
    // End-user handoff: the viewer's own fill work vs. an RFQ parked with them.
    'end-user-fill': { icon: UserCheck, chipClass: 'bg-ai-chip border-robbie-border', iconClass: 'text-robbie-ink' },
    'end-user-pending': { icon: Hourglass, chipClass: 'bg-neutral-bg border-neutral-border', iconClass: 'text-neutral-ink' },
};

const AttentionList: React.FC<AttentionListProps> = ({ items, onAction, className = '' }) => {
    const visible = items.slice(0, MAX_ITEMS);
    if (visible.length === 0) return null;

    return (
        <section className={`overflow-hidden rounded-card border border-line bg-surface ${className}`}>
            <div className="border-b border-line-divider-soft px-s6 py-[12px] max-md:px-s5">
                <span className="text-[13.5px] font-semibold text-ink">Needs your attention · {items.length}</span>
            </div>
            {visible.map((item) => {
                const { icon: Icon, chipClass, iconClass } = chipFor[item.reason];
                return (
                    <button
                        key={item.id}
                        type="button"
                        onClick={() => onAction(item)}
                        className="flex w-full flex-wrap items-center gap-[12px] border-b border-line-divider px-s6 py-[10px] text-left transition-colors last:border-b-0 hover:bg-surface-hover focus:outline-none focus-visible:bg-surface-hover max-md:px-s5 max-md:py-s4"
                    >
                        <span className={`flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-[7px] border ${chipClass}`}>
                            <Icon className={`h-[13px] w-[13px] ${iconClass}`} />
                        </span>
                        <span className="text-[13px] font-semibold text-ink max-md:basis-[calc(100%-38px)] max-md:truncate">{item.name}</span>
                        {item.insight ? (
                            <Badge tone="robbie">Robbie: {item.insight}</Badge>
                        ) : (
                            <span className="text-[12.5px] text-ink-secondary max-md:min-w-0 max-md:truncate">{item.label}</span>
                        )}
                        <span className="ml-auto text-[12px] font-semibold text-action">{item.actionLabel} →</span>
                    </button>
                );
            })}
        </section>
    );
};

export default AttentionList;
